import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Center, Grid, Group, Modal, Select , TextInput, Title, Divider } from "@mantine/core";

import LoginLogoutApi from "../../api/LoginLogoutApi";
import SettingsApi from "../../api/SettingsApi";
import { visiUserData } from "../../GeneralFunction";
import { HelpTip } from "../../components/GeneralComponent/GeneralComponent";
import { ONBOARDING_ENABLED } from "../../onboardingConfig";

export function AccountSetup({ onComplete }) {
  const navigate = useNavigate();
  const [opened, setOpened] = useState(true);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    country: "",
    state: "",
    city: "",
    zip: "",
    mobile: "",
    timezone: "",
  });
  type Errors = {
    firstName?: string;
    lastName?: string;
    email?: string;
    company?: string;
    country?: string;
    state?: string;
    city?: string;
    zip?: string;
    timezone?: string;
    mobile?: string;
  };
  const [countryList, setCountryList] = useState([{ country_id : '', country_name : 'Please Select Country' }]);
  const [stateList, setStateList] = useState([{ state_id : '', state_name : 'Please Select State' }]);
  const [timeZoneList, setTimeZoneList] = useState([{ zone : '', zone_time : 'Please Select Timezone' }]);
  const [errors, setErrors] = useState<Errors>({});

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const getTimeZoneList = () => {
    SettingsApi.gettimezonelist()
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
        }
      }
    })
    .then(data => {
      if(data.status === 1 && data.data && data.data.length > 0){
        setTimeZoneList(data.data);
      }
    })
    .catch(error => {
      console.log(error);
    });
  };

  const getCountryList = () => {
    SettingsApi.getcountrylist()
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
        }
      }
    })
    .then(data => {
      if(data.status === 1 && data.data && data.data.length > 0){
        setCountryList(data.data);
      }
    })
    .catch(error => {
      console.log(error);
    });
  };

  const getStateList = () => {
    SettingsApi.getstatelist({
      country_id : formData.country
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
        }
      }
    })
    .then(data => {
      if(data.status === 1 && data.data && data.data.length > 0){
        setStateList(data.data);
      }
    })
    .catch(error => {
      console.log(error);
    });
  }

  const getAccountSetupDetails = () => {
    LoginLogoutApi.getaccountsetupdetails()
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if(data.status === 1){
          setFormData(prev => ({
            ...prev,
            firstName: data.data.first_name,
            lastName: data.data.last_name,
            email: data.data.email,
            company: data.data.company_name,
            country: data.data.country,
            state: data.data.state,
            city: data.data.city,
            zip: data.data.zip,
            mobile: data.data.phone,
            timezone: data.data.timezone
          }));
        }
      })
      .catch(error => {
        console.error("API Error:", error);
      })
  };

  const validateForm = () => {
    const newErrors: Errors = {};

    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.company.trim()) newErrors.company = "Company name is required";
    if (!formData.country) newErrors.country = "Country is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.zip.trim()) newErrors.zip = "Zip code is required";
    if (!formData.timezone) newErrors.timezone = "Timezone is required";
    if (!formData.mobile.trim()) newErrors.mobile = "Mobile number is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    getCountryList();
    getTimeZoneList();
    getAccountSetupDetails();
  }, []);

  useEffect(() => {
    if(formData.country !== ''){
      getStateList();
    }
  }, [formData.country]);

  const handleSave = () => {
    if(validateForm()){
      LoginLogoutApi.saveaccountsetup({
        first_name: formData.firstName,
        last_name: formData.lastName,
        country: formData.country,
        company_name: formData.company,
        state: formData.state,
        city: formData.city,
        zip: formData.zip,
        phone: formData.mobile,
        timezone: formData.timezone
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if(data.status === 1){
          if(data.data.first_time_login === 0){
            const ud = visiUserData();
            if(ONBOARDING_ENABLED && ud && Number(ud.onboarding_completed) === 0){
              navigate('/onboarding');
            }else{
              navigate('/settings');
            }
          }
        }
      })
      .catch(error => {
        console.error("API Error:", error);
      })
    }
  };


  return (
    <>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={
          <Group spacing="xs">
            <Title order={4} fw={700} c="gray.8">
              SET UP YOUR ACCOUNT
            </Title>
            <HelpTip helpId={179} />
          </Group>
        }
        size="70%"
        styles={{
          content: { height: "90vh" },
        }}
        overlayProps={{
          opacity: 0.55,
          blur: 3,
        }}
      >
        <Divider mb="md" color="orange" />

        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label="First name:"
              placeholder="First name"
              value={formData.firstName}
              onChange={handleChange("firstName")}
              error={errors.firstName}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Last name:"
              placeholder="Last name"
              value={formData.lastName}
              onChange={handleChange("lastName")}
              error={errors.lastName}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <TextInput
              label="Email:"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange("email")}
              error={errors.email}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Company Name:"
              placeholder="Company Name"
              value={formData.company}
              onChange={handleChange("company")}
              error={errors.company}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <Select
              label="Country:"
              placeholder="Select country"
              data={countryList.map(country => ({
                value: country.country_id,
                label: country.country_name
              }))}
              value={formData.country}
              onChange={(val) => setFormData({ ...formData, country: val, state: '' })}
              error={errors.country}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Select
              label="State:"
              placeholder="Select state"
              data={stateList.map(state => ({
                value: state.state_id,
                label: state.state_name
              }))}
              value={formData.state}
              onChange={(val) => setFormData({ ...formData, state: val })}
              error={errors.state}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <TextInput
              label="City:"
              placeholder="City"
              value={formData.city}
              onChange={handleChange("city")}
              error={errors.city}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Zip Code:"
              placeholder="Zip Code"
              value={formData.zip}
              onChange={handleChange("zip")}
              error={errors.zip}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <Select
              label="Timezone:"
              placeholder="Select timezone"
              data={timeZoneList.map(timezone => ({
                value: timezone.zone,
                label: timezone.zone_time
              }))}
              value={formData.timezone}
              onChange={(val) =>
                setFormData({ ...formData, timezone: val })
              }
              error={errors.timezone}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Mobile Number:"
              placeholder="Mobile Number"
              value={formData.mobile}
              onChange={handleChange("mobile")}
              error={errors.mobile}
            />
          </Grid.Col>
        </Grid>

        <Group mt="lg">
          <Button color="teal" w={555} onClick={handleSave}>Save</Button>
          <Center
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%"
            }}
          >
            <img src="img/lock-logo.png" alt="Lock logo" />
          </Center>
        </Group>
      </Modal>
    </>
  );
}
